import type { MeasuredGround, TerrainArtifact, TerrainControlPoint } from '@/reference/SitePipeline';
import { mercatorMetersToLatLng, offsetBetween, offsetLatLng, type LatLng } from './GeoMath';
import { frameContains, frameToSite, siteToFrame, type MapFrame } from './MapFrame';
import {
  gridCellLatLng,
  gridHeight,
  gridStatistics,
  ringAreaSquareMeters,
  type ElevationGrid,
  type ParcelBoundary,
} from './SiteData';

/**
 * Reading topography and boundaries out of the files people actually have.
 *
 * ## Why files come before endpoints
 *
 * Every public elevation service is a different authentication story, a different raster encoding
 * and a different set of coverage gaps — and all of them can export one of the three formats below.
 * So the import path is worth building first: it works today, in every country, for any source
 * including a surveyor's own file, and when a live endpoint is wired up it will hand this module the
 * same {@link ElevationGrid} rather than a fourth code path.
 *
 * Three formats, chosen because between them they cover what DEM portals and survey software emit:
 *
 * - **Esri ASCII grid** (`.asc`, `.grd`) — the lingua franca. USGS, OpenTopography and QGIS all
 *   export it; the header is six lines of plain text and the body is numbers.
 * - **XYZ / CSV** (`.xyz`, `.csv`, `.txt`) — point clouds and spot heights, either projected metres
 *   or lat/lng. Regularly-spaced points are re-gridded; irregular ones become control points
 *   directly, which is what a surveyor's spot levels actually are.
 * - **GeoJSON** — a boundary ring, or a set of point features carrying an elevation property.
 *
 * ## The part that matters: what it becomes
 *
 * A measured grid becomes **two** things, and the distinction is the whole point of this module.
 *
 * - {@link gridToMeasuredGround} resamples it onto the site's own axes at something near its native
 *   resolution, and the compiler bakes that straight into the heightfield. This is the ground.
 * - {@link gridToControlPoints} thins it to the two dozen readings that *describe* the landform,
 *   which is what stages 3–5 are shown and what the project description quotes.
 *
 * The reduction used to be the only path, and it was quietly destroying the thing it was importing:
 * the compiler's lattice is 145 × 145, so reconstructing 21,000 vertices by blending 24 readings
 * into procedural noise erases every swale, crown and retaining bank in the source. That is the
 * right treatment for a *guess* — two dozen guesses should not masquerade as a survey — and the
 * wrong one for a survey. Now the guess path and the measured path are different code.
 *
 * Scattered {@link SpotHeight}s stay control points, and that is not an oversight. Six spot levels
 * across a garden are not a surface; where an instrument was put is already a judgement, and
 * interpolating them into a lattice would invent ground between them and then present it as
 * measured.
 *
 * Pure parsing and arithmetic; the file reading itself belongs to the caller.
 */

export class ElevationImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ElevationImportError';
  }
}

export type ImportedTopography =
  | { kind: 'grid'; grid: ElevationGrid }
  | { kind: 'points'; points: SpotHeight[]; label: string };

/** One surveyed height, before it is anything the pipeline understands. */
export interface SpotHeight {
  at: LatLng;
  elevationMeters: number;
  label?: string;
}

/**
 * Read whatever was dropped on the panel.
 *
 * Dispatches on content rather than on extension, because `.txt` and `.csv` are used for all of
 * these and a file renamed on the way out of a portal should still work.
 */
export function parseTopographyFile(name: string, text: string): ImportedTopography {
  const trimmed = text.trim();
  if (trimmed.length === 0) throw new ElevationImportError(`${name} is empty.`);

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return { kind: 'points', points: parseGeoJsonHeights(name, trimmed), label: name };
  }
  if (/^\s*ncols\b/i.test(trimmed)) {
    return { kind: 'grid', grid: parseAsciiGrid(name, trimmed) };
  }
  return { kind: 'points', points: parseXyz(name, trimmed), label: name };
}

/* ------------------------------- Esri ASCII grid ------------------------------- */

/**
 * An Esri ASCII grid.
 *
 * The header can express its origin as a *corner* or a *centre*, and its cell size in degrees or in
 * projected metres, and getting either wrong shifts the whole terrain by half a cell or scales it by
 * a factor of 100,000. Both are handled explicitly; the units are inferred from the magnitude of
 * `cellsize`, which is unambiguous in practice — nobody has a 0.001 m DEM and nobody has a 30 degree
 * one.
 */
export function parseAsciiGrid(name: string, text: string): ElevationGrid {
  const lines = text.split(/\r?\n/);
  const header = new Map<string, number>();
  let index = 0;

  while (index < lines.length) {
    const line = (lines[index] ?? '').trim();
    const match = /^([A-Za-z_]+)\s+(-?[\d.eE+]+)$/.exec(line);
    if (!match) break;
    header.set((match[1] ?? '').toLowerCase(), Number(match[2]));
    index += 1;
  }

  const cols = header.get('ncols');
  const rows = header.get('nrows');
  const cellsize = header.get('cellsize');
  if (!cols || !rows || !cellsize) {
    throw new ElevationImportError(
      `${name} is missing an ASCII grid header (ncols, nrows, cellsize). Is it really a .asc file?`,
    );
  }

  const nodata = header.get('nodata_value') ?? -9999;
  const values = (lines.slice(index).join(' ').match(/-?[\d.]+(?:[eE][-+]?\d+)?/g) ?? []).map(Number);
  if (values.length < cols * rows) {
    throw new ElevationImportError(
      `${name} declares ${cols} × ${rows} cells but carries only ${values.length} values.`,
    );
  }

  // Degrees or metres, decided by magnitude: a geographic cell size is a fraction of a degree, a
  // projected one is metres. Nothing real sits between them.
  const geographic = Math.abs(cellsize) < 0.01;
  const xll = header.get('xllcorner') ?? header.get('xllcenter') ?? 0;
  const yll = header.get('yllcorner') ?? header.get('yllcenter') ?? 0;
  const isCorner = header.has('xllcorner');

  // The header names the SOUTH-west corner; the grid is stored north-to-south, so the origin this
  // module wants is the north-west cell.
  const southWest: LatLng = geographic
    ? { lat: yll, lng: xll }
    : mercatorMetersToLatLng(xll, yll);
  const cellMeters = geographic ? cellsize * 111320 * Math.cos((southWest.lat * Math.PI) / 180) : cellsize;
  const half = isCorner ? cellMeters / 2 : 0;
  const origin = offsetLatLng(southWest, half, -(rows - 1) * cellMeters - half);

  const heights = new Float32Array(cols * rows);
  for (let i = 0; i < cols * rows; i += 1) {
    const value = values[i] as number;
    heights[i] = value === nodata ? Number.NaN : value;
  }

  return {
    origin,
    cellSizeMeters: cellMeters,
    cols,
    rows,
    heights,
    sourceId: 'elevation-file',
    label: name,
    ...(geographic ? { datum: 'geographic (WGS 84 degrees)' } : { datum: 'projected metres' }),
  };
}

/* ---------------------------------- XYZ / CSV ---------------------------------- */

/**
 * Loose point data: `x y z`, `lat,lng,elev`, or a CSV with a header naming its columns.
 *
 * Column order is the trap. `x y z` from a DEM export is longitude-then-latitude; a surveyor's
 * `lat, lng` is the other way round. Guessed from range when there is no header — a value outside
 * ±90 cannot be a latitude — and read from the header when there is one, which is the only way to
 * get it right for a site between 0 and 90 in both.
 */
export function parseXyz(name: string, text: string): SpotHeight[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0 && !line.startsWith('#'));
  if (lines.length === 0) throw new ElevationImportError(`${name} has no readable rows.`);

  const first = lines[0] as string;
  const headerCells = splitRow(first);
  const named = columnsFromHeader(headerCells);
  const body = named ? lines.slice(1) : lines;

  const points: SpotHeight[] = [];
  let latFirst = named?.latFirst ?? null;

  for (const line of body) {
    const cells = splitRow(line).map(Number);
    if (cells.length < 3 || cells.some((value) => !Number.isFinite(value))) continue;

    const a = cells[named?.a ?? 0] as number;
    const b = cells[named?.b ?? 1] as number;
    const elevation = cells[named?.elevation ?? 2] as number;

    // Decided once, from the first row that can settle it, then held — so a file cannot flip
    // convention halfway through and produce a scatter of points on two continents.
    latFirst ??= Math.abs(a) <= 90 && Math.abs(b) > 90 ? true : Math.abs(b) <= 90 && Math.abs(a) > 90 ? false : null;

    const projected = Math.abs(a) > 180 || Math.abs(b) > 90;
    const at = projected
      ? mercatorMetersToLatLng(a, b)
      : latFirst
        ? { lat: a, lng: b }
        : { lat: b, lng: a };

    if (Math.abs(at.lat) > 90 || Math.abs(at.lng) > 180) continue;
    points.push({ at, elevationMeters: elevation });
  }

  if (points.length === 0) {
    throw new ElevationImportError(
      `${name} has rows but none of them read as a coordinate and a height. Expected "lat, lng, elevation" or "x y z".`,
    );
  }
  return points;
}

interface XyzColumns {
  a: number;
  b: number;
  elevation: number;
  latFirst: boolean | null;
}

function columnsFromHeader(cells: readonly string[]): XyzColumns | null {
  const lower = cells.map((cell) => cell.trim().toLowerCase());
  const find = (...names: string[]): number => lower.findIndex((cell) => names.includes(cell));

  const lat = find('lat', 'latitude', 'y');
  const lng = find('lng', 'lon', 'long', 'longitude', 'x');
  const elevation = find('elev', 'elevation', 'z', 'height', 'alt', 'altitude');
  if (lat < 0 || lng < 0 || elevation < 0) return null;

  return { a: lat, b: lng, elevation, latFirst: true };
}

function splitRow(line: string): string[] {
  return line.trim().split(/[,;\t ]+/);
}

/* ----------------------------------- GeoJSON ----------------------------------- */

/** Spot heights from a GeoJSON of points, reading whichever elevation property it happens to use. */
export function parseGeoJsonHeights(name: string, text: string): SpotHeight[] {
  const features = geoJsonFeatures(name, text);
  const points: SpotHeight[] = [];

  for (const feature of features) {
    const geometry = feature.geometry as { type?: string; coordinates?: unknown } | undefined;
    if (geometry?.type !== 'Point') continue;
    const coordinates = geometry.coordinates as number[] | undefined;
    if (!coordinates || coordinates.length < 2) continue;

    const properties = (feature.properties ?? {}) as Record<string, unknown>;
    // GeoJSON's own third ordinate first, then whatever the exporter called the attribute.
    const elevation =
      coordinates[2] ??
      numberProperty(properties, ['elevation', 'elev', 'z', 'height', 'alt', 'value']);
    if (elevation === undefined || !Number.isFinite(elevation)) continue;

    points.push({
      at: { lat: coordinates[1] as number, lng: coordinates[0] as number },
      elevationMeters: elevation,
      ...(typeof properties.name === 'string' ? { label: properties.name } : {}),
    });
  }

  if (points.length === 0) {
    throw new ElevationImportError(`${name} has no point features carrying an elevation.`);
  }
  return points;
}

/**
 * A boundary ring from a GeoJSON polygon.
 *
 * Takes the outer ring of the first polygon and drops the repeated closing vertex — GeoJSON closes
 * its rings, this codebase's polygons do not, and a duplicated last vertex produces a zero-length
 * edge that shows up much later as a rendering artifact nobody can trace.
 */
export function parseBoundaryGeoJson(name: string, text: string): ParcelBoundary {
  const features = geoJsonFeatures(name, text);

  for (const feature of features) {
    const geometry = feature.geometry as { type?: string; coordinates?: unknown } | undefined;
    const rings =
      geometry?.type === 'Polygon'
        ? (geometry.coordinates as number[][][])
        : geometry?.type === 'MultiPolygon'
          ? ((geometry.coordinates as number[][][][])[0] ?? null)
          : null;
    const outer = rings?.[0];
    if (!outer || outer.length < 4) continue;

    const ring: LatLng[] = outer.map((pair) => ({ lat: pair[1] as number, lng: pair[0] as number }));
    const first = ring[0] as LatLng;
    const last = ring[ring.length - 1] as LatLng;
    if (ring.length > 3 && first.lat === last.lat && first.lng === last.lng) ring.pop();

    const properties = (feature.properties ?? {}) as Record<string, unknown>;
    return {
      ring,
      sourceId: 'parcel-file',
      label:
        (typeof properties.address === 'string' && properties.address) ||
        (typeof properties.name === 'string' && properties.name) ||
        name,
      // Filled in by the caller, which is the only place that knows the frame to measure against.
      areaSquareMeters: 0,
      simulated: false,
    };
  }

  throw new ElevationImportError(`${name} has no polygon to read as a boundary.`);
}

/** Area cannot be computed without a frame to project into, so it is filled in on arrival. */
export function measureBoundary(frame: MapFrame, boundary: ParcelBoundary): ParcelBoundary {
  return { ...boundary, areaSquareMeters: ringAreaSquareMeters(frame, boundary.ring) };
}

function geoJsonFeatures(name: string, text: string): { geometry?: unknown; properties?: unknown }[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new ElevationImportError(`${name} is not valid JSON.`);
  }
  const root = parsed as { type?: string; features?: unknown[]; geometry?: unknown };
  if (Array.isArray(root.features)) return root.features as { geometry?: unknown }[];
  if (root.geometry !== undefined) return [root];
  if (root.type !== undefined) return [{ geometry: root }];
  throw new ElevationImportError(`${name} is JSON but not GeoJSON.`);
}

function numberProperty(properties: Record<string, unknown>, names: readonly string[]): number | undefined {
  for (const key of Object.keys(properties)) {
    if (!names.includes(key.toLowerCase())) continue;
    const value = Number(properties[key]);
    if (Number.isFinite(value)) return value;
  }
  return undefined;
}

/* ------------------------- Becoming something the pipeline uses ------------------------- */

export interface ControlPointOptions {
  /** How many readings to keep. The compiler blends smoothly; more is not better past ~30. */
  maxPoints?: number;
  /** Ignore relief below this, so measurement noise on a flat site does not become bumps. */
  minReliefMeters?: number;
}

/**
 * A grid reduced to the readings the terrain compiler actually wants.
 *
 * The reduction is a decimation on a coarse lattice rather than a "pick the extremes" search, for a
 * reason worth stating: the compiler weights readings by inverse distance, so an even spread
 * reconstructs a slope faithfully while a set of peaks and pits reconstructs a golf course. Extremes
 * are what a person would pick and they are the wrong answer here.
 *
 * Heights are re-datumed to the grid's median so the site sits at elevation zero — the pipeline's
 * terrain is relative, and handing it 43 m of absolute altitude would raise the whole plot into
 * nothing.
 */
export function gridToControlPoints(
  grid: ElevationGrid,
  frame: MapFrame,
  options: ControlPointOptions = {},
): TerrainControlPoint[] {
  const maxPoints = options.maxPoints ?? 24;
  const minRelief = options.minReliefMeters ?? 0.15;
  const stats = gridStatistics(grid);
  if (stats.sampled === 0 || stats.reliefMeters < minRelief) return [];

  // A lattice roughly √maxPoints on a side, so the readings land evenly over the ground rather than
  // clustering wherever the grid happens to be dense.
  const side = Math.max(2, Math.floor(Math.sqrt(maxPoints)));
  const stepCol = Math.max(1, Math.floor(grid.cols / side));
  const stepRow = Math.max(1, Math.floor(grid.rows / side));
  const radius = Math.max(2, (Math.max(stepCol, stepRow) * grid.cellSizeMeters) / 1.5);

  const points: TerrainControlPoint[] = [];
  for (let row = Math.floor(stepRow / 2); row < grid.rows; row += stepRow) {
    for (let col = Math.floor(stepCol / 2); col < grid.cols; col += stepCol) {
      const height = gridHeight(grid, col, row);
      if (!Number.isFinite(height)) continue;

      const at = gridCellLatLng(grid, col, row);
      if (!frameContains(frame, at)) continue;

      const site = frameToSite(frame, at);
      points.push({
        x: round2(site.x),
        z: round2(site.z),
        elevationMeters: round2(height - stats.median),
        radiusMeters: round2(radius),
      });
    }
  }
  return points;
}

/**
 * Scattered spot heights as control points.
 *
 * No decimation: irregular readings are already a person's or an instrument's choice of where the
 * ground was worth measuring, and thinning them would throw that judgement away. Only the datum and
 * the frame clipping are applied.
 */
export function spotHeightsToControlPoints(
  points: readonly SpotHeight[],
  frame: MapFrame,
): TerrainControlPoint[] {
  const inside = points.filter((point) => frameContains(frame, point.at));
  if (inside.length === 0) return [];

  const sorted = inside.map((point) => point.elevationMeters).sort((a, b) => a - b);
  const datum = sorted[Math.floor(sorted.length / 2)] as number;
  // Reach scaled to how sparse the readings are: a dozen levels across a garden each speak for a
  // wider patch of ground than a hundred would.
  const radius = Math.max(2, frame.widthMeters / Math.max(2, Math.sqrt(inside.length) * 1.6));

  return inside.map((point) => {
    const site = frameToSite(frame, point.at);
    const control: TerrainControlPoint = {
      x: round2(site.x),
      z: round2(site.z),
      elevationMeters: round2(point.elevationMeters - datum),
      radiusMeters: round2(radius),
    };
    if (point.label) control.notes = point.label;
    return control;
  });
}

export interface MeasuredGroundOptions {
  /**
   * Ceiling on samples per axis. Not a quality knob — the compiler's own lattice is 145 across, so
   * going finer than this buys nothing and costs an artifact big enough to be awkward to hold and
   * to show. It only binds on very large frames; at garden scale the source's resolution is always
   * the coarser of the two and wins.
   */
  maxAxisSamples?: number;
}

/**
 * A DEM resampled onto the site's axes, ready for the compiler to bake.
 *
 * **The frame's projection is applied here, once.** Every lattice point is converted to a coordinate
 * by {@link siteToFrame} and then looked up in the source grid, which means rotation and quad
 * rectification are handled by the same arithmetic that handles everything else and the compiler
 * never learns what a latitude is. A north-up source under a 30°-turned frame simply comes out
 * turned.
 *
 * Sample spacing is the coarser of the source's cell size and the ceiling, never finer than the
 * source: upsampling a 10 m DEM to a 0.3 m lattice would manufacture detail that is not in the
 * measurement, and bilinear interpolation makes that invention look convincingly smooth.
 *
 * Returns `null` when the source has no readings at all inside the frame — the caller should say so
 * rather than bake a site-sized hole.
 */
export function gridToMeasuredGround(
  grid: ElevationGrid,
  frame: MapFrame,
  options: MeasuredGroundOptions = {},
): MeasuredGround | null {
  const stats = gridStatistics(grid);
  if (stats.sampled === 0) return null;

  const maxAxis = Math.max(2, Math.trunc(options.maxAxisSamples ?? 192));
  const spacing = Math.max(
    grid.cellSizeMeters,
    Math.max(frame.widthMeters, frame.lengthMeters) / maxAxis,
  );
  const cols = Math.min(maxAxis, Math.max(2, Math.round(frame.widthMeters / spacing) + 1));
  const rows = Math.min(maxAxis, Math.max(2, Math.round(frame.lengthMeters / spacing) + 1));

  // Derived so the lattice spans the frame exactly, rather than falling a fraction of a cell short
  // of the far edge and leaving the compiler to extrapolate a strip it did not need to.
  const spacingXMeters = frame.widthMeters / (cols - 1);
  const spacingZMeters = frame.lengthMeters / (rows - 1);
  const originX = -frame.widthMeters / 2;
  const originZ = -frame.lengthMeters / 2;

  const heights: (number | null)[] = new Array<number | null>(cols * rows);
  let sampled = 0;

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const at = siteToFrame(frame, originX + col * spacingXMeters, originZ + row * spacingZMeters);
      const height = sampleGridBilinear(grid, at);
      const value = height === null ? null : round2(height - stats.median);
      heights[row * cols + col] = value;
      if (value !== null) sampled += 1;
    }
  }

  if (sampled === 0) return null;

  return { originX, originZ, spacingXMeters, spacingZMeters, cols, rows, heights, label: grid.label };
}

/**
 * The source grid sampled at a coordinate, bilinearly.
 *
 * Missing neighbours are dropped and the remaining weights renormalized rather than treated as zero,
 * so the rim of a NODATA lake stays at the height of the bank instead of being dragged toward the
 * datum. All four missing is a hole, and stays one.
 */
function sampleGridBilinear(grid: ElevationGrid, at: LatLng): number | null {
  const { eastMeters, southMeters } = offsetBetween(grid.origin, at);
  const gc = eastMeters / grid.cellSizeMeters;
  const gr = southMeters / grid.cellSizeMeters;
  if (gc < -0.5 || gr < -0.5 || gc > grid.cols - 0.5 || gr > grid.rows - 0.5) return null;

  const col = Math.floor(gc);
  const row = Math.floor(gr);
  const fx = gc - col;
  const fz = gr - row;

  let weightSum = 0;
  let valueSum = 0;
  for (const [dc, dr, weight] of [
    [0, 0, (1 - fx) * (1 - fz)],
    [1, 0, fx * (1 - fz)],
    [0, 1, (1 - fx) * fz],
    [1, 1, fx * fz],
  ] as const) {
    if (weight <= 0) continue;
    // Clamped rather than rejected, so a sample landing exactly on the far edge reads the edge cell
    // instead of falling into the gap between "inside the grid" and "has four neighbours".
    const height = gridHeight(
      grid,
      Math.min(grid.cols - 1, Math.max(0, col + dc)),
      Math.min(grid.rows - 1, Math.max(0, row + dr)),
    );
    if (!Number.isFinite(height)) continue;
    weightSum += weight;
    valueSum += weight * height;
  }
  return weightSum <= 0 ? null : valueSum / weightSum;
}

/**
 * The whole terrain stage, from measured ground.
 *
 * When a lattice is supplied it is the ground and the control points are the *description* — the
 * compiler bakes the former and stages 3–5 read the latter. `roughness` drops to zero in that case
 * because there is nothing left for procedural noise to do; without a lattice it stays low but
 * non-zero, since two dozen readings genuinely do need texture between them.
 *
 * `elevationAmplitude` is the measured relief either way. It no longer decides anything when a
 * lattice is present — the baked heights do — but a stored parameter that contradicts the terrain
 * it supposedly generated is a trap for the next reader.
 */
export function topographyToArtifact(
  points: readonly TerrainControlPoint[],
  label: string,
  measured?: MeasuredGround | null,
): TerrainArtifact {
  const relief = measured ? measuredRelief(measured) : pointRelief(points);
  return {
    ...(measured ? { measured } : {}),
    elevationAmplitude: round2(Math.min(20, relief)),
    roughness: measured ? 0 : 0.15,
    controlPoints: [...points],
    notes: measured
      ? `Measured, not inferred: ${measured.cols} × ${measured.rows} samples at ` +
        `${measured.spacingXMeters.toFixed(2)} m from ${label}, re-datumed to the site's median ` +
        `ground level and baked directly. The ${points.length} control points alongside describe ` +
        'the same landform; they do not shape it.'
      : `Measured, not inferred: ${points.length} readings from ${label}, re-datumed to the site's ` +
        'median ground level. The procedural noise under them is texture only.',
  };
}

function pointRelief(points: readonly TerrainControlPoint[]): number {
  return points.reduce((max, point) => Math.max(max, Math.abs(point.elevationMeters)), 0);
}

function measuredRelief(ground: MeasuredGround): number {
  let min = Infinity;
  let max = -Infinity;
  for (const height of ground.heights) {
    if (height === null) continue;
    min = Math.min(min, height);
    max = Math.max(max, height);
  }
  return Number.isFinite(min) ? max - min : 0;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
